import { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import { logSecurityEvent } from '../../services/admin';
import styles from './Admin.module.css';

export function AdminSettings() {
  const [maintenance, setMaintenance] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'sending' }
    | { kind: 'success'; recipient: string; resendId: string | null }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  const handleSendTestEmail = async () => {
    const current = auth.currentUser;
    if (!current) {
      setTestEmailStatus({ kind: 'error', message: 'Not signed in.' });
      return;
    }
    setTestEmailStatus({ kind: 'sending' });
    try {
      const token = await current.getIdToken();
      const res = await fetch('/api/admin/test-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setTestEmailStatus({ kind: 'error', message: data?.error || `Request failed (${res.status})` });
        return;
      }
      setTestEmailStatus({
        kind: 'success',
        recipient: data.recipient || current.email || 'unknown',
        resendId: data.resendId || null,
      });
    } catch (err: any) {
      setTestEmailStatus({ kind: 'error', message: err?.message || 'Unexpected error' });
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      const snap = await getDoc(doc(db, 'system_config', 'main'));
      if (snap.exists()) {
        setMaintenance(snap.data().maintenanceMode || false);
      }
    };
    fetchSettings();
  }, []);

  const handleMaintenanceToggle = async (mode: boolean) => {
    if (window.confirm(`Are you sure you want to turn ${mode ? 'ON' : 'OFF'} maintenance mode?`)) {
      setMaintenance(mode);
      await setDoc(doc(db, 'system_config', 'main'), { maintenanceMode: mode }, { merge: true });
      await logSecurityEvent('MAINTENANCE_TOGGLED', { enabled: mode });
    }
  };

  return (
    <div>
      <header className={styles.pageHeader}>
        <h1>System Settings</h1>
        <p style={{ color: 'var(--text-3)' }}>Secure configuration management. API keys are masked.</p>
      </header>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <h3>AI Diagnostic Provider</h3>
          <select className={styles.actionSelect} style={{ width: '100%', marginTop: '10px' }}>
            <option>NVIDIA NIM (Active)</option>
            <option>OpenAI GPT-4</option>
            <option>Anthropic Claude</option>
          </select>
        </div>

        <div className={styles.statCard}>
          <h3>System Maintenance</h3>
          <select 
            value={maintenance ? "true" : "false"}
            onChange={(e) => handleMaintenanceToggle(e.target.value === "true")}
            className={styles.actionSelect} 
            style={{ width: '100%', marginTop: '10px' }}
          >
            <option value="false">Live (Operational)</option>
            <option value="true">Maintenance Mode</option>
          </select>
        </div>

        <div className={styles.statCard}>
          <h3>Stripe Payment Gateway</h3>
          <div style={{ marginTop: '10px', color: 'var(--text-3)', fontSize: '0.9rem' }}>
            Status: <span style={{ color: 'var(--text-3)', fontWeight: 600 }}>Not Configured</span>
          </div>
        </div>
      </div>

      <div className={styles.tableContainer} style={{ padding: '24px' }}>
        <h2>Environment Secrets</h2>
        <div style={{ background: 'rgba(255, 165, 0, 0.1)', border: '1px solid orange', padding: '16px', borderRadius: '8px', color: 'orange', fontSize: '0.85rem', marginBottom: '24px', lineHeight: '1.5' }}>
          <strong>Security Notice:</strong> Sensitive API keys (NVIDIA, Stripe, Firebase Admin) are managed exclusively through <strong>Vercel and Firebase backend environment variables</strong>. For security reasons, these secrets cannot be viewed or edited directly from the browser admin panel.
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ opacity: 0.5 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 700 }}>NVIDIA API Key</label>
            <div style={{ fontSize: '0.9rem', fontFamily: 'monospace' }}>Configured in Vercel Deployment</div>
          </div>
          <div style={{ opacity: 0.5 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 700 }}>Stripe Secret Key</label>
            <div style={{ fontSize: '0.9rem', fontFamily: 'monospace' }}>Awaiting Configuration</div>
          </div>
        </div>
      </div>

      <div className={styles.tableContainer} style={{ padding: '24px', marginTop: '24px' }}>
        <h2>Email Delivery Diagnostics</h2>
        <p style={{ color: 'var(--text-3)', fontSize: '0.9rem', marginBottom: '16px' }}>
          Send a diagnostic test email via Resend to your signed-in admin address. No payment is processed and no credits are granted.
        </p>
        <button
          type="button"
          className={styles.actionBtn}
          onClick={handleSendTestEmail}
          disabled={testEmailStatus.kind === 'sending'}
        >
          {testEmailStatus.kind === 'sending' ? 'Sending...' : 'Send Test Email'}
        </button>
        {testEmailStatus.kind === 'success' && (
          <div style={{ marginTop: '16px', background: 'rgba(57, 255, 20, 0.1)', border: '1px solid var(--accent)', color: 'var(--accent)', padding: '12px', borderRadius: '8px', fontSize: '0.9rem' }}>
            <strong>Sent.</strong> Recipient: {testEmailStatus.recipient}
            {testEmailStatus.resendId ? <> · Resend ID: <code>{testEmailStatus.resendId}</code></> : null}
          </div>
        )}
        {testEmailStatus.kind === 'error' && (
          <div style={{ marginTop: '16px', background: 'rgba(255, 80, 80, 0.1)', border: '1px solid #ff5050', color: '#ff8080', padding: '12px', borderRadius: '8px', fontSize: '0.9rem' }}>
            <strong>Failed.</strong> {testEmailStatus.message}
          </div>
        )}
      </div>
    </div>
  );
}
